/**
 * Trip-preparation cost honesty labels.
 * Stored on task `custom_fields.cost_certainty` (no schema migration required).
 */

export type CostCertainty = "verified" | "estimated" | "user" | "unknown"

export const COST_CERTAINTY_VALUES: readonly CostCertainty[] = [
  "verified",
  "estimated",
  "user",
  "unknown",
] as const

/** Dates generated by Wundrlst heuristics (not government deadlines). */
export type DateKind = "government_deadline" | "planning_recommendation" | "user"

export function parseCostCertainty(raw: unknown): CostCertainty {
  if (typeof raw !== "string") return "unknown"
  const v = raw.trim().toLowerCase()
  if (v === "verified" || v === "estimated" || v === "user" || v === "unknown") return v
  return "unknown"
}

export function parseDateKind(raw: unknown): DateKind {
  if (typeof raw !== "string") return "planning_recommendation"
  const v = raw.trim().toLowerCase()
  if (v === "government_deadline" || v === "planning_recommendation" || v === "user") return v
  return "planning_recommendation"
}

export function costCertaintyFromCustomFields(
  customFields: Record<string, unknown> | null | undefined
): CostCertainty {
  if (!customFields) return "unknown"
  return parseCostCertainty(customFields.cost_certainty)
}

/** Merge honesty markers into task custom_fields without wiping other keys. */
export function withCostHonesty(
  fields: Record<string, unknown>,
  opts: {
    costCertainty?: CostCertainty
    dateKind?: DateKind
    requirementSource?: "verified_metadata" | "enriched_steps" | "generic_template" | "user"
  }
): Record<string, unknown> {
  const next = { ...fields }
  if (opts.costCertainty) next.cost_certainty = opts.costCertainty
  if (opts.dateKind) next.date_kind = opts.dateKind
  if (opts.requirementSource) next.requirement_source = opts.requirementSource
  return next
}
